import { useState, useEffect, useRef } from "react";
import {
  IconButton,
  Badge,
  Menu,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Divider,
  Typography,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import axios from "axios";
import { useRecoilValue, useRecoilState } from "recoil";
import { authState, notificationHasUnread } from "../../Recoil/RecoilState";
import { baseURL } from "../../utils/StaticVariables";
import useNotificationWebSocket from "../../hooks/useNotificationWebSocket";

const NotificationMenu = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [hasUnread, setHasUnread] = useRecoilState(notificationHasUnread);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { token } = useRecoilValue(authState);
  const open = Boolean(anchorEl);

  // 🚨 Alert state
  const [alertMessage, setAlertMessage] = useState("");
  // ⏰ Timer state for delayed close button
  const [showCloseButton, setShowCloseButton] = useState(false);
  const [countdown, setCountdown] = useState(10);

  // 🔑 Track last shown alert to prevent duplicates
  const lastShownAlertRef = useRef({
    camera_name: null,
    timestamp: null
  });

  // ⏰ Effect to show close button after countdown
  useEffect(() => {
    if (alertMessage) {
      setCountdown(10);
      const interval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            setShowCloseButton(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [alertMessage]);

  // ✅ Helper function to check if alert should be shown
  const shouldShowAlert = (notification) => {
    const lastShown = lastShownAlertRef.current;
    const currentCameraName = notification.camera_name;
    const currentTimestamp = notification.timestamp?.getTime();

    // Show if it's a different camera OR different timestamp
    if (
      lastShown.camera_name !== currentCameraName ||
      lastShown.timestamp !== currentTimestamp
    ) {
      // Update the tracking ref
      lastShownAlertRef.current = {
        camera_name: currentCameraName,
        timestamp: currentTimestamp
      };
      return true;
    }

    // Same camera and same timestamp - don't show
    console.log(
      `🚫 Duplicate alert blocked: ${currentCameraName} at ${notification.timestamp?.toLocaleTimeString()}`
    );
    return false;
  };

  // ✅ WebSocket Listener
  useNotificationWebSocket({
    onMessage: (data) => {

      if (Array.isArray(data?.notifications)) {
        const parsedNotes = data?.notifications?.map((n) => ({
          id: n.id,
          message: n.message,
          timestamp: new Date(n.timestamp * 1000),
          camera_name: n.camera_name,
          read: false,
        }));

        setNotifications((prev) => [...parsedNotes, ...prev]);

        // 🚨 Check for Fire/Smoke keywords and deduplication
        const firstNote = parsedNotes[0];
        const isCriticalAlert =
          firstNote.message.toLowerCase().includes("fire") ||
          firstNote.message.toLowerCase().includes("smoke");

        if (isCriticalAlert) {
          // Only show if not duplicate
          if (shouldShowAlert(firstNote)) {
            console.log(
              `🔥 Showing alert: ${firstNote.camera_name} at ${firstNote.timestamp?.toLocaleTimeString()}`
            );
            setAlertMessage(firstNote);
          }
        }
      }

      setHasUnread(true);
    },
  });

  const handleOpen = async (e) => {
    setAnchorEl(e.currentTarget);
    setHasUnread(false);
    await fetchNotifications();
  };

  const handleClose = () => {
    setAnchorEl(null);
    setError("");
  };

  const handleCloseAlert = () => {
    setAlertMessage("");
    setShowCloseButton(false);
    setCountdown(10);
    // Note: We don't reset lastShownAlertRef here to maintain deduplication
  };

  const fetchNotifications = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${baseURL}notify`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (data.status === "success") {
        const parsed = data.notifications.map((n) => ({
          id: n.id,
          message: n.message,
          timestamp: new Date(n.timestamp * 1000),
          camera_name: n.camera_name,
          read: n.read,
        }));
        setNotifications(parsed);
      } else {
        setError("Failed to load notifications");
      }
    } catch (err) {
      setError("An error occurred while fetching notifications.");
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (date) =>
    `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;

  return (
    <>
      <IconButton color="inherit" onClick={handleOpen}>
        <Badge
          variant="dot"
          color="error"
          overlap="circular"
          invisible={!hasUnread}
        >
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: { width: 360, maxHeight: "65vh", overflowY: "auto" },
        }}
      >
        <List
          subheader={
            <ListSubheader component="div" sx={{ py: 1 }}>
              <Typography variant="h6">Notifications</Typography>
            </ListSubheader>
          }
        >
          {loading && (
            <ListItem>
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={20} />
                <Typography variant="body2">Loading...</Typography>
              </Box>
            </ListItem>
          )}

          {error && (
            <ListItem>
              <ListItemText
                primary={
                  <Typography color="error" variant="body2">
                    {error}
                  </Typography>
                }
              />
            </ListItem>
          )}

          {!loading && !error && notifications.length === 0 && (
            <ListItem>
              <ListItemText primary="No notifications" />
            </ListItem>
          )}

          {!loading &&
            !error &&
            notifications.map((note) => (
              <div key={note.id}>
                <ListItem
                  alignItems="flex-start"
                  sx={{
                    borderLeft: note.read
                      ? "4px solid transparent"
                      : (theme) => `4px solid ${theme.palette.primary.main}`,
                    backgroundColor: note.read
                      ? "inherit"
                      : "rgba(16,170,157,0.1)",
                    mb: 0.5,
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        fontWeight={note.read ? 400 : 600}
                      >
                        {note.message}
                      </Typography>
                    }
                    secondary={
                      <>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {formatTime(note.timestamp)}
                        </Typography>
                        {note.camera_name && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Camera: {note.camera_name}
                          </Typography>
                        )}
                      </>
                    }
                  />
                </ListItem>
                <Divider component="li" />
              </div>
            ))}
        </List>
      </Menu>

      {/* 🚨 Fire/Smoke Alert Popup */}
      <Dialog
        open={!!alertMessage}
        onClose={() => {}} // Prevent closing by clicking outside
        disableEscapeKeyDown // Prevent closing with Escape key
      >
        <DialogTitle sx={{ color: "red" }}>🚨 Emergency Alert</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
            {alertMessage.message}
          </Typography>
          {alertMessage.camera_name && (
            <Typography variant="body2" color="text.secondary">
              Camera: {alertMessage.camera_name}
            </Typography>
          )}
          {alertMessage.timestamp && (
            <Typography variant="body2" color="text.secondary">
              Time: {formatTime(alertMessage.timestamp)}
            </Typography>
          )}

          {/* ⏰ Timer display */}
          {!showCloseButton && (
            <Box sx={{ mt: 2, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                Close button will appear in {countdown} seconds
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {/* ✅ Show close button only after countdown */}
          {showCloseButton && (
            <Button
              onClick={handleCloseAlert}
              color="error"
              variant="contained"
            >
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NotificationMenu;